import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { get, post, del, fileUrl } from "@/lib/api";
import { QuestionEditor } from "@/pages/teacher/QuestionBank";
import { Badge, statusTone } from "@/components/common";
import { X, Trash2, PenSquare, CheckCircle2, Loader2 } from "lucide-react";

// Reusable modal to view a test's full contents (questions + answers).
// canManage: show edit/remove per question + delete whole test.
export default function TestDetailModal({ testId, canManage = false, onClose, onChanged }) {
  const [t, setT] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => get(`/tests/${testId}/detail`).then(setT).catch(() => toast.error("Could not load test"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [testId]);

  const removeQ = async (qid) => { await post(`/tests/${testId}/remove-question`, { question_id: qid }); toast.success("Question removed from test"); load(); onChanged && onChanged(); };
  const deleteTest = async () => { await del(`/tests/${testId}`); toast.success("Test deleted"); onClose(); onChanged && onChanged(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl p-6 max-w-3xl w-full my-6">
        {!t ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-700" /></div> : (
          <>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-display font-bold text-xl text-slate-900">{t.title}</h3>
                <div className="flex flex-wrap gap-2 mt-1 text-xs">
                  <Badge tone={statusTone(t.status)}>{t.status}</Badge>
                  <Badge tone="indigo">{t.test_type}</Badge>
                  <Badge tone="slate">{t.total_questions} Qs</Badge>
                  <Badge tone="slate">{t.total_marks} marks</Badge>
                  <Badge tone="slate">{t.duration_min} min</Badge>
                  {t.is_free ? <Badge tone="green">Free</Badge> : <Badge tone="amber">₹{t.price}</Badge>}
                </div>
              </div>
              <button onClick={onClose}><X className="h-5 w-5" /></button>
            </div>

            {(t.detailed_sections || []).map((s) => (
              <div key={s.id} className="mb-4">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">{s.name} · {s.questions.length} questions</p>
                <div className="space-y-3">
                  {s.questions.map((q, i) => (
                    <div key={q.id} className="border border-slate-200 rounded-xl p-3" data-testid={`detail-q-${i}`}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-slate-800">Q{i + 1}. {q.question_text}</p>
                        {canManage && (
                          <div className="flex gap-2 shrink-0">
                            <button onClick={() => setEditing(q)} data-testid={`edit-q-detail-${i}`} className="text-indigo-700"><PenSquare className="h-4 w-4" /></button>
                            <button onClick={() => removeQ(q.id)} data-testid={`remove-q-detail-${i}`} className="text-red-500"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        )}
                      </div>
                      {q.question_image && <img src={fileUrl(q.question_image.replace("/api", ""))} alt="" className="max-h-36 rounded-lg border mt-2" />}
                      <div className="mt-2 space-y-1">
                        {(q.options || []).map((o) => (
                          <p key={o.key} className={`text-sm px-2 py-1 rounded flex items-center gap-2 ${q.correct_answer?.includes(o.key) ? "bg-emerald-50 text-emerald-800" : "text-slate-600"}`}>
                            <span className="font-bold">{o.key}.</span> {o.text} {q.correct_answer?.includes(o.key) && <CheckCircle2 className="h-3.5 w-3.5 ml-auto" />}
                          </p>
                        ))}
                      </div>
                      {q.explanation && <p className="text-xs text-slate-500 mt-2">💡 {q.explanation}</p>}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {canManage && (
              <div className="flex justify-between border-t border-slate-100 pt-4 mt-2">
                <button onClick={deleteTest} data-testid="delete-test-detail-btn" className="text-red-600 font-semibold text-sm flex items-center gap-1"><Trash2 className="h-4 w-4" /> Delete entire test</button>
                <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 font-semibold text-slate-600 text-sm">Close</button>
              </div>
            )}
          </>
        )}
      </div>
      {editing && <QuestionEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
